import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  toast,
  ToastContainer,
  type ToastOptions,
  type Id,
} from 'react-toastify';
import { type AxiosError, isAxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import styles from 'src/designSystem/BumpyCard.module.scss';
import { ClassNameValue, twMerge } from 'src/utils/twMerge';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import { stripIdsFromUrl } from 'src/utils/url';

interface ToastArgs extends Omit<ToastOptions, 'type'> {
  message: ReactNode;
  description?: ReactNode;
  'data-testid'?: string;
}

type ErrorArgs = (
  | (ToastArgs & { error?: undefined })
  | (Omit<ToastArgs, 'message'> & {
    error: unknown;
    message: ReactNode;
  })
) & { errorId?: string };
type StaticFn = (args: ToastArgs) => Id;

export interface NotificationContextType {
  showSuccess: StaticFn;
  showInfo: StaticFn;
  showWarning: StaticFn;
  show: StaticFn;
  showError: (args: ErrorArgs) => Id;
  close: (key: Id) => void;
}

export const NotificationContext = createContext<NotificationContextType>({
  showSuccess: () => {
    throw new Error('Not implemented');
  },
  showInfo: () => {
    throw new Error('Not implemented');
  },
  showWarning: () => {
    throw new Error('Not implemented');
  },
  showError: () => {
    throw new Error('Not implemented');
  },
  show: () => {
    throw new Error('Not implemented');
  },
  close: () => {
    throw new Error('Not implemented');
  },
});

const formatToastContent = (args: ToastArgs) => {
  const content = args.description ? (
    <div className={'flex flex-col gap-1 py-2'}>
      <div className="font-semibold text-base">{args.message}</div>
      <div className="text-sm font-medium mt-1">{args.description}</div>
    </div>
  ) : (
    args.message
  );

  if (args['data-testid']) {
    return <div data-testid={args['data-testid']}>{content}</div>;
  }
  return content;
};

const toastClassname = `px-4 py-2 rounded-2xl text-structural-darkest font-bold border-2 border-structural-darkest mb-2 min-h-[60px] ${styles.bumpyCardShadow}`;
const defaultOptions: ToastOptions = {
  position: 'bottom-center',
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  closeButton: true,
  className: toastClassname,
  progressClassName: 'opacity-30',
};

const NotificationContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();

  const showSuccess = useCallback((args: ToastArgs) => {
    const { 'data-testid': _dataTestId, ...toastOptions } = args;
    return toast.success(formatToastContent(args), {
      ...defaultOptions,
      progressClassName: 'bg-green-darkest',
      ...toastOptions,
      className: twMerge(
        toastClassname,
        'border-green-darkest shadow-green-darkest',
        (args.className as ClassNameValue | undefined) ?? '',
      ),
    });
  }, []);

  const showInfo = useCallback((args: ToastArgs) => {
    const { 'data-testid': _dataTestId, ...toastOptions } = args;
    return toast.info(formatToastContent(args), {
      ...defaultOptions,
      progressClassName: 'bg-structural-darkest',
      ...toastOptions,
      className: twMerge(
        toastClassname,
        'border-structural-darkest shadow-structural-darkest',
        (args.className as ClassNameValue | undefined) ?? '',
      ),
    });
  }, []);

  const showWarning = useCallback((args: ToastArgs) => {
    const { 'data-testid': _dataTestId, ...toastOptions } = args;
    return toast.warning(formatToastContent(args), {
      ...defaultOptions,
      progressClassName: 'bg-warning-darkest',
      ...toastOptions,
      className: twMerge(
        toastClassname,
        'border-warning-darkest shadow-warning-darkest',
        (args.className as ClassNameValue | undefined) ?? '',
      ),
    });
  }, []);

  const show = useCallback((args: ToastArgs) => {
    const { 'data-testid': _dataTestId, ...toastOptions } = args;
    return toast(formatToastContent(args), {
      ...defaultOptions,
      progressClassName: 'bg-structural-darkest',
      ...toastOptions,
      className: twMerge(
        toastClassname,
        'border-structural-darkest shadow-structural-darkest',
        (args.className as ClassNameValue | undefined) ?? '',
      ),
    });
  }, []);

  const showError = useCallback(
    (args: ErrorArgs) => {
      const { 'data-testid': _dataTestId, ...toastOptions } = args;

      const errorId =
        args.errorId ||
        (typeof args.message === 'string' ? args.message : undefined);
      const pagePath = window.location.pathname;
      const pageRoute = stripIdsFromUrl(pagePath);

      if (args.error && isAxiosError(args.error)) {
        const error: AxiosError = args.error;
        const httpStatus = error.response?.status ?? error.status;
        const method = error.config?.method?.toUpperCase();
        const rawUrl = error.config?.url;
        const apiRoute =
          method && rawUrl
            ? `${method} ${stripIdsFromUrl(rawUrl)}`
            : undefined;
        logAnalyticsEvent('error_toast_shown', {
          errorMessageId: errorId || `error.http.${httpStatus}`,
          errorType: 'http',
          httpStatus,
          apiRoute,
          pagePath,
          pageRoute,
        });
        /*
        t('error.http.400')
        t('error.http.401')
        t('error.http.403')
        t('error.http.404')
        t('error.http.422')
        t('error.http.500')
        t('error.http.503')
        t('error.http.504')
        t('error.http.default')
        */
        const description = t(
          `error.http.${error.response?.status ?? error.status}`,
          {
            defaultValue: t('error.http.default'),
          },
        );
        return toast.error(formatToastContent({ ...args, description }), {
          ...defaultOptions,
          progressClassName: 'bg-red-darkest',
          ...toastOptions,
          className: twMerge(
            toastClassname,
            'border-red-darkest',
            (args.className as ClassNameValue | undefined) ?? '',
          ),
        });
      }

      else {
        logAnalyticsEvent('error_toast_shown', {
          errorMessageId: errorId,
          errorType: 'app',
          pagePath,
          pageRoute,
        });
      }

      return toast.error(formatToastContent(args), {
        ...defaultOptions,
        progressClassName: 'bg-red-darkest',
        ...toastOptions,
        className: twMerge(
          toastClassname,
          'border-red-darkest shadow-red-darkest',
          (args.className as ClassNameValue | undefined) ?? '',
        ),
      });
    },
    [t],
  );

  const close = useCallback((key: Id) => {
    toast.dismiss(key);
  }, []);

  const value = useMemo(
    () => ({ showSuccess, showInfo, showWarning, showError, show, close }),
    [showSuccess, showInfo, showWarning, showError, show, close],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer
        className="pointer-events-none [&>*]:pointer-events-auto bottom-[calc(var(--safe-bottom)+56px)] left-[var(--app-column-left,0px)] right-auto transform-none w-[var(--app-column-width,100%)] max-w-full px-4"
        toastClassName="bg-transparent p-0 shadow-none mb-0"
      />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  return useContext(NotificationContext);
};

export default NotificationContextProvider;
